import { Injector } from '@angular/core';
import { Router } from '@angular/router';
import { AlertHandlerService } from '@finxone-platform/shared/services';
import {
  APP_ZONES,
  ActionButtonOutcomesType,
  NavigationOptionEnum,
  UiZoneWidgetAttributeConfig,
  formatNameAsUrl,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { catchError, firstValueFrom, of, take, tap } from 'rxjs';
import { GetAccountRequests } from '../../../actions/savings-account.action';
import { SavingsAccountService } from '../../../services/savings-account-service/savings-account.service';
import { SavingsAccountState } from '../../../state/savings-account.state';
import { handleRedirection } from '../cta-redirection.utils';

export async function reviewAccountRequests(injector: Injector, attri: UiZoneWidgetAttributeConfig) {
  const store = injector.get(Store);
  const router = injector.get(Router);
  const savingsAccountService = injector.get(SavingsAccountService);
  const alertHandlerService = injector.get(AlertHandlerService);
  const navigationConfig = attri.widgetProperties?.actionNavigationConfigurations;
  const oneRequestFoundPageUrl =
    navigationConfig?.[ActionButtonOutcomesType.SUCCESS]?.selectedPageUrl ?? `sumsub`;
  const oneRequestFoundZone =
    navigationConfig?.[ActionButtonOutcomesType.SUCCESS]?.selectedZone ?? APP_ZONES.PROFILE;

  const multipleRequestsFoundPageUrl =
    navigationConfig?.[ActionButtonOutcomesType.ALTERNATE_1]?.selectedPageUrl ?? `review-account-requests`;
  const multipleRequestsFoundZone =
    navigationConfig?.[ActionButtonOutcomesType.ALTERNATE_1]?.selectedZone ?? APP_ZONES.LANDING;

  const noRequestsFoundPageUrl =
    navigationConfig?.[ActionButtonOutcomesType.FAILURE]?.selectedPageUrl ?? `home`;
  const noRequestsFoundZone =
    navigationConfig?.[ActionButtonOutcomesType.FAILURE]?.selectedZone ?? APP_ZONES.LANDING;
  await firstValueFrom(store.dispatch(new GetAccountRequests()).pipe(take(1)));

  const accountRequests = store.selectSnapshot(SavingsAccountState.getAccountRequests);

  if (!Array.isArray(accountRequests) || accountRequests.length === 0) {
    router.navigateByUrl(`/zones/${formatNameAsUrl(noRequestsFoundZone)}/${noRequestsFoundPageUrl}`);
    alertHandlerService.showAlertFn(
      'success',
      "You've got no pending account applications to review",
      'Nothing here for you to do',
    );
    return;
  }

  if (accountRequests?.length === 1) {
    const applicationId = accountRequests[0].id;
    if (applicationId) {
      savingsAccountService
        .getAccountRequestToken(applicationId)
        .pipe(
          tap((response) => {
            const token = response.sumsubAccessToken;
            const nextRoute = `/zones/${formatNameAsUrl(
              oneRequestFoundZone,
            )}/${oneRequestFoundPageUrl}?sumsubAccessToken=${token}`;
            router.navigateByUrl(nextRoute);
          }),
          catchError((error) => {
            if (
              attri.widgetProperties?.['actionNavigationConfigurations']?.[
                ActionButtonOutcomesType.FAILURE
              ] &&
              attri.widgetProperties?.['actionNavigationConfigurations']?.[ActionButtonOutcomesType.FAILURE]
                .selectedNavigationOption !== NavigationOptionEnum.USE_AS_BLANK
            ) {
              handleRedirection(attri.widgetProperties, injector, ActionButtonOutcomesType.FAILURE, true);
            }
            console.error('Failed to get account request token:', error);
            return of(null);
          }),
        )
        .subscribe();
    }
  } else {
    const nextRoute = `/zones/${formatNameAsUrl(multipleRequestsFoundZone)}/${multipleRequestsFoundPageUrl}`;
    router.navigateByUrl(nextRoute);
  }
}
